import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import Select from "react-select";
import {
  Modal,
  Button,
  Form,
  Spinner,
  Alert,
  Row,
  Col,
} from "react-bootstrap";
import { BASE_URL } from "../../Config";
import "bootstrap-icons/font/bootstrap-icons.css";

const AssignPackageToCompanyModal = ({
  show,
  onHide,
  packages = [],
  companies = [],
  onAssigned,
}) => {
  const [packageIds, setPackageIds] = useState([]);
  const [companyProfileId, setCompanyProfileId] = useState("");
  const [percentageOffer, setPercentageOffer] = useState("");
  const [priceInAmount, setPriceInAmount] = useState("");
  const [priceOfWebdocInAmount, setPriceOfWebdocInAmount] = useState("");
  const [assignedPackageIds, setAssignedPackageIds] = useState([]);
  const [loadingAssignedPackages, setLoadingAssignedPackages] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show) {
      setPackageIds([]);
      setCompanyProfileId("");
      setPercentageOffer("");
      setPriceInAmount("");
      setPriceOfWebdocInAmount("");
      setAssignedPackageIds([]);
      setLoadingAssignedPackages(false);
      setLoading(false);
    }
  }, [show]);

  const getPackageOptionId = (pkg) =>
    pkg.packageid ?? pkg.packageId ?? pkg.id ?? pkg.packagesId;

  useEffect(() => {
    setPackageIds([]);

    if (!show || !companyProfileId) {
      setAssignedPackageIds([]);
      return;
    }

    const loadAssignedPackageIds = async () => {
      setLoadingAssignedPackages(true);
      try {
        const response = await axios.get(
          `${BASE_URL}RetailCompanyPackage?id=${companyProfileId}`,
          { headers: { accept: "*/*" } }
        );
        const assignedPackages = Array.isArray(response.data?.packages)
          ? response.data.packages
          : Array.isArray(response.data?.data)
            ? response.data.data
            : Array.isArray(response.data)
              ? response.data
              : [];

        setAssignedPackageIds(
          assignedPackages
            .map(getPackageOptionId)
            .filter((id) => id != null)
            .map(String)
        );
      } catch (error) {
        console.error("Load company packages for assignment error:", error);
        setAssignedPackageIds([]);
      } finally {
        setLoadingAssignedPackages(false);
      }
    };

    loadAssignedPackageIds();
  }, [show, companyProfileId]);

  const availablePackages = packages.filter((pkg) => {
    const id = getPackageOptionId(pkg);
    return id != null && !assignedPackageIds.includes(String(id));
  });

  const packageOptions = availablePackages.map((pkg) => {
    const id = getPackageOptionId(pkg);
    return {
      value: String(id),
      label: `${pkg.packageName || pkg.name || `Package #${id}`}${
        pkg.packagePrice != null ? ` — Rs. ${pkg.packagePrice}` : ""
      }`,
    };
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!packageIds.length || !companyProfileId) {
      Swal.fire("Error", "Please select at least one package and a company.", "error");
      return;
    }

    setLoading(true);
    try {
      const responses = [];
      for (const selectedPackageId of packageIds) {
        const params = {
          PackageId: Number(selectedPackageId),
          CompanyProfileId: companyProfileId,
        };

        if (percentageOffer !== "") params.PercentageOffer = Number(percentageOffer);
        if (priceInAmount !== "") params.PriceInAmount = Number(priceInAmount);
        if (priceOfWebdocInAmount !== "") {
          params.PriceOfWebdocInAmount = Number(priceOfWebdocInAmount);
        }

        const response = await axios.post(`${BASE_URL}AssignPackageToCompany`, null, {
          params,
          headers: { accept: "*/*" },
        });
        responses.push(response.data);
      }

      const successfulCount = responses.filter(
        (response) => response?.responseCode === "0000"
      ).length;
      const ok = successfulCount === packageIds.length;
      Swal.fire({
        icon: ok ? "success" : "error",
        title: ok ? "Packages Assigned" : "Some Assignments Failed",
        text: ok
          ? `${successfulCount} package(s) assigned to the company successfully.`
          : `${successfulCount} of ${packageIds.length} package(s) were assigned.`,
      });

      if (ok) {
        onHide();
        if (typeof onAssigned === "function") onAssigned();
      }
    } catch (error) {
      console.error("AssignPackageToCompany error:", error);
      Swal.fire(
        "Error",
        error.response?.data?.message ||
          "Unable to assign package to company. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-building-add me-2" />
          Assign Package to Company
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Alert variant="light" className="border small">
            Select a package from your assigned list and an assigned company.
          </Alert>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold small">Package *</Form.Label>
            <Select
              isMulti
              options={packageOptions}
              value={packageOptions.filter((option) => packageIds.includes(option.value))}
              onChange={(selectedOptions) =>
                setPackageIds((selectedOptions || []).map((option) => option.value))
              }
              placeholder={
                loadingAssignedPackages ? "Loading packages..." : "Select packages"
              }
              noOptionsMessage={() => "No packages available"}
              isDisabled={!companyProfileId || loadingAssignedPackages}
              closeMenuOnSelect={false}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold small">Company *</Form.Label>
            <Form.Select
              value={companyProfileId}
              onChange={(e) => setCompanyProfileId(e.target.value)}
              required
            >
              <option value="">Select Company</option>
              {companies.map((c) => (
                <option key={c.companyProfileId} value={c.companyProfileId}>
                  {c.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Row className="g-2">
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Offer %</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={percentageOffer}
                  onChange={(e) => setPercentageOffer(e.target.value)}
                  placeholder="Optional"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Price</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={priceInAmount}
                  onChange={(e) => setPriceInAmount(e.target.value)}
                  placeholder="Optional"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold small">WebDoc Price</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={priceOfWebdocInAmount}
                  onChange={(e) => setPriceOfWebdocInAmount(e.target.value)}
                  placeholder="Optional"
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Assigning...
              </>
            ) : (
              <>
                <i className="bi bi-check2 me-1" />
                Assign to Company
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AssignPackageToCompanyModal;
